import { formatMessage, type MessageDescriptor } from "./i18n-identity-status";

export type IdentityStatusCanonicalValue = "active" | "inactive";
export type IdentityStatusTone = "success" | "default" | "warning";
export type IdentityStatusValueEnumStatus = "Success" | "Default" | "Warning";

export type IdentityStatusDisplay = {
  canonicalValue: IdentityStatusCanonicalValue | undefined;
  label: string;
  tone: IdentityStatusTone;
  valueEnumStatus: IdentityStatusValueEnumStatus;
  raw: string;
  isDefined: boolean;
};

type IdentityStatusLabelResolver = (descriptor: MessageDescriptor) => string;

const IDENTITY_STATUS_LABEL_DESCRIPTORS: Record<IdentityStatusCanonicalValue, MessageDescriptor> = {
  active: { id: "platform.admin.identity.values.active" },
  inactive: { id: "platform.admin.identity.values.inactive" },
};

const IDENTITY_STATUS_TONE_MAP: Record<IdentityStatusCanonicalValue, IdentityStatusTone> = {
  active: "success",
  inactive: "default",
};

const IDENTITY_STATUS_VALUE_ENUM_STATUS_MAP: Record<IdentityStatusCanonicalValue, IdentityStatusValueEnumStatus> = {
  active: "Success",
  inactive: "Default",
};

const UNDEFINED_STATUS_DESCRIPTOR = { id: "platform.admin.identity.values.undefinedStatus" } as const;

function normalizeIdentityStatusRawValue(value: string | number | null | undefined) {
  return String(value ?? "").trim();
}

function resolveIdentityStatusLabel(
  value: IdentityStatusCanonicalValue,
  resolveLabel: IdentityStatusLabelResolver,
) {
  return resolveLabel(IDENTITY_STATUS_LABEL_DESCRIPTORS[value]);
}

function resolveUndefinedStatusLabel(resolveLabel: IdentityStatusLabelResolver) {
  return resolveLabel(UNDEFINED_STATUS_DESCRIPTOR);
}

function createIdentityStatusDisplay(
  value: string | number | null | undefined,
  resolveLabel: IdentityStatusLabelResolver,
): IdentityStatusDisplay {
  const raw = normalizeIdentityStatusRawValue(value);
  if (raw === "active" || raw === "inactive") {
    const canonicalValue: IdentityStatusCanonicalValue = raw;
    return {
      canonicalValue,
      label: resolveIdentityStatusLabel(canonicalValue, resolveLabel),
      tone: IDENTITY_STATUS_TONE_MAP[canonicalValue],
      valueEnumStatus: IDENTITY_STATUS_VALUE_ENUM_STATUS_MAP[canonicalValue],
      raw,
      isDefined: true,
    };
  }

  const undefinedLabel = resolveUndefinedStatusLabel(resolveLabel);
  return {
    canonicalValue: undefined,
    label: raw ? `${undefinedLabel} (${raw})` : undefinedLabel,
    tone: "warning",
    valueEnumStatus: "Warning",
    raw,
    isDefined: false,
  };
}

function resolveSharedIdentityStatusLabel(descriptor: MessageDescriptor) {
  return formatMessage(descriptor);
}

export function getIdentityStatusDisplay(
  value: string | number | null | undefined,
  resolveLabel: IdentityStatusLabelResolver = resolveSharedIdentityStatusLabel,
) {
  return createIdentityStatusDisplay(value, resolveLabel);
}

export function getIdentityStatusLabel(
  value: string | number | null | undefined,
  resolveLabel?: IdentityStatusLabelResolver,
) {
  return getIdentityStatusDisplay(value, resolveLabel).label;
}

export function getIdentityStatusTone(value: string | number | null | undefined) {
  return getIdentityStatusDisplay(value).tone;
}

export function getIdentityStatusValueEnumStatus(value: string | number | null | undefined) {
  return getIdentityStatusDisplay(value).valueEnumStatus;
}

export function normalizeIdentityStatusValue(value: string | number | null | undefined) {
  return getIdentityStatusDisplay(value).canonicalValue;
}

export function buildIdentityStatusValues() {
  return ["active", "inactive"] as const;
}
